/**
 * MobileSocialHintDetail Component for LOOPINO AI Platform
 * Mobile page for viewing detailed social hint information
 */

import React, { useState, useCallback, useEffect } from 'react';
import './MobileSocialHintDetail.css';
import useLanguage from '../../../hooks/useLanguage';

// Import icons
import iconArrowLeft from '../../../assets/images/icons/icon-arrow-left.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconVideo from '../../../assets/images/icons/icon-video-generator.svg';
import iconClose from '../../../assets/images/icons/icon-close.svg';
import iconAI from '../../../assets/images/icons/icon-ai.svg';

const MobileSocialHintDetail = ({
  hint,
  onBack
}) => {
  const { t } = useLanguage();

  // State management
  const [uploadedFiles, setUploadedFiles] = useState(new Map()); // Map of sceneId -> file
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [embedsLoading, setEmbedsLoading] = useState({});
  const [carouselStates, setCarouselStates] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createdVideoUrl, setCreatedVideoUrl] = useState(null);
  const [creationProgress, setCreationProgress] = useState(0);
  const [isQueued, setIsQueued] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);

  // Initialize embed loading states and carousel states when hint changes
  useEffect(() => {
    if (hint?.embedUrls) {
      const embedLoadingStates = {};
      const carouselInitialStates = {};

      hint.embedUrls.forEach((url, index) => {
        embedLoadingStates[`detail_${hint.id}_${index}`] = true;
      });

      // Initialize carousel to first item
      carouselInitialStates[hint.id] = 0;

      setEmbedsLoading(embedLoadingStates);
      setCarouselStates(carouselInitialStates);
    }
  }, [hint]);

  // Handle file upload
  const handleFileUpload = useCallback((sceneId, file) => {
    if (!file) {
      return;
    }
    console.log(`[MobileSocialHintDetail] File uploaded for scene ${sceneId}:`, file.name);
    const fileData = {
      id: `${sceneId}_${Date.now()}`,
      name: file.name,
      type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file',
      url: URL.createObjectURL(file),
      size: file.size,
      uploadedAt: new Date().toISOString()
    };

    setUploadedFiles(prev => new Map(prev.set(sceneId, fileData)));
  }, []);

  // Handle file removal
  const handleFileRemove = useCallback((sceneId) => {
    console.log(`[MobileSocialHintDetail] Removing file for scene ${sceneId}`);
    setUploadedFiles(prev => {
      const newMap = new Map(prev);
      const file = newMap.get(sceneId);
      if (file && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
      }
      newMap.delete(sceneId);
      return newMap;
    });
  }, []);

  // Handle create content click
  const handleCreateClick = useCallback(() => {
    console.log('[MobileSocialHintDetail] Create content clicked for hint:', hint?.id);
    setShowCreateModal(true);
  }, [hint]);

  // Handle create content confirmation
  const handleCreateConfirm = useCallback(async () => {
    console.log('[MobileSocialHintDetail] Creating content with files:', uploadedFiles);
    setShowCreateModal(false);
    setIsCreating(true);
    setCreationProgress(0);

    // Simulate creation progress
    const progressInterval = setInterval(() => {
      setCreationProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          // Simulate video creation completion
          setTimeout(() => {
            const mockVideoUrl = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDE=';
            setCreatedVideoUrl(mockVideoUrl);
            setIsCreating(false);
          }, 500);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
  }, [uploadedFiles]);

  // Handle queue button click
  const handleQueueClick = useCallback(() => {
    console.log('[MobileSocialHintDetail] Adding to queue');
    setIsQueued(true);
    setShowQueueModal(true);
  }, []);

  // Handle queue modal close
  const handleQueueModalClose = useCallback(() => {
    setShowQueueModal(false);
  }, []);

  // Cleanup uploaded files on unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach(file => {
        if (file.url.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
      });
    };
  }, [uploadedFiles]);

  // Handle create modal close
  const handleCreateModalClose = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  // Convert Instagram URL to embed URL
  const getInstagramEmbedUrl = (url) => {
    if (!url) return null;

    // Handle Instagram post URLs
    if (url.includes('instagram.com/p/')) {
      return `${url}embed/`;
    }

    // Handle Instagram reel URLs
    if (url.includes('instagram.com/reel/')) {
      return `${url}embed/`;
    }

    return url;
  };

  // Handle embed load
  const handleEmbedLoad = useCallback((embedKey) => {
    setEmbedsLoading(prev => ({
      ...prev,
      [embedKey]: false
    }));
  }, []);

  // Handle embed error
  const handleEmbedError = useCallback((embedKey) => {
    setEmbedsLoading(prev => ({
      ...prev,
      [embedKey]: false
    }));
  }, []);

  // Handle carousel navigation - copied from MobileSocialHint
  const handleCarouselNext = useCallback((hintId, maxIndex) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: Math.min((prev[hintId] || 0) + 1, maxIndex)
    }));
  }, []);

  const handleCarouselPrev = useCallback((hintId) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: Math.max((prev[hintId] || 0) - 1, 0)
    }));
  }, []);

  // Handle carousel dot click
  const handleCarouselDotClick = useCallback((hintId, index) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: index
    }));
  }, []);

  // Handle horizontal swipe for carousel
  const handleCarouselTouchStart = useCallback((e, hintId) => {
    const touch = e.touches[0];
    e.currentTarget.carouselStartX = touch.clientX;
    e.currentTarget.carouselStartY = touch.clientY;
    e.currentTarget.carouselStartTime = Date.now();
  }, []);

  const handleCarouselTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const startX = e.currentTarget.carouselStartX || touch.clientX;
    const startY = e.currentTarget.carouselStartY || touch.clientY;
    const deltaX = Math.abs(touch.clientX - startX);
    const deltaY = Math.abs(touch.clientY - startY);

    // If horizontal swipe is dominant, prevent vertical scroll
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const handleCarouselTouchEnd = useCallback((e, hintId, maxIndex) => {
    const touch = e.changedTouches[0];
    const endX = touch.clientX;
    const endY = touch.clientY;
    const startX = e.currentTarget.carouselStartX || endX;
    const startY = e.currentTarget.carouselStartY || endY;
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const deltaTime = Date.now() - (e.currentTarget.carouselStartTime || 0);

    console.log(`[Carousel ${hintId}] Touch end - deltaX: ${deltaX}, deltaY: ${deltaY}, time: ${deltaTime}ms`);

    // Only process if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      const minSwipeDistance = 30; // Reduced threshold for easier swipe
      const maxSwipeTime = 800; // Increased time for easier swipe

      if (Math.abs(deltaX) > minSwipeDistance && deltaTime < maxSwipeTime) {
        console.log(`[Carousel ${hintId}] Valid swipe detected: ${deltaX > 0 ? 'right' : 'left'}`);

        if (deltaX > 0) {
          // Swipe right - go to previous
          handleCarouselPrev(hintId);
        } else {
          // Swipe left - go to next
          handleCarouselNext(hintId, maxIndex);
        }
      }
    }
  }, [handleCarouselPrev, handleCarouselNext]);

  const getEmbedTypeLabel = useCallback((embedType) => {
    if (!embedType) {
      return '';
    }
    return t(`socialHint.common.embeds.types.${embedType}`, {
      defaultValue: t('socialHint.common.embeds.types.default')
    });
  }, [t]);

  const getIframeTitle = useCallback((index, title, embedType) => {
    const networkLabel = getEmbedTypeLabel(embedType) || embedType;
    return t('socialHint.common.embeds.iframeTitle', {
      index: index + 1,
      title,
      network: networkLabel
    });
  }, [getEmbedTypeLabel, t]);

  // Get scene instructions based on hint category
  const getSceneInstructions = useCallback(() => {
    const sceneExamples = t('socialHint.detail.scenes.examples', { returnObjects: true }) || {};
    const category = hint?.category;
    if (category && Array.isArray(sceneExamples[category]) && sceneExamples[category].length) {
      return sceneExamples[category];
    }
    return sceneExamples.default || [];
  }, [hint?.category, t]);

  // Render file upload component
  const renderFileUpload = (sceneId, label) => {
    const uploadedFile = uploadedFiles.get(sceneId);

    const getFileTypeLabel = (type) => t(`socialHint.common.fileMeta.types.${type}`, {
      defaultValue: type
    });

    const getFileSizeLabel = (size) => t('socialHint.common.fileMeta.size', {
      size: (size / 1024 / 1024).toFixed(1)
    });

    return (
      <div className="mobile-social-hint-detail-upload-section">
        {uploadedFile ? (
          <div className="mobile-project-file-item">
            <div className="mobile-project-file-preview">
              {uploadedFile.type === 'image' ? (
                <img
                  src={uploadedFile.url}
                  alt={uploadedFile.name}
                  className="mobile-project-file-image"
                />
              ) : uploadedFile.type === 'video' ? (
                <div className="mobile-project-file-video">
                  <video
                    src={uploadedFile.url}
                    className="mobile-project-file-video-element"
                    muted
                    preload="metadata"
                  />
                  <div className="mobile-project-file-video-overlay">
                    <img src={iconVideo} alt={t('socialHint.common.alt.video')} />
                  </div>
                </div>
              ) : (
                <div className="mobile-project-file-placeholder">
                  <img src={iconImage} alt={t('socialHint.common.alt.file')} />
                </div>
              )}
            </div>
            <div className="mobile-project-file-info">
              <div className="mobile-project-file-name">{uploadedFile.name}</div>
              <div className="mobile-project-file-meta">
                {t('socialHint.common.fileMeta.format', {
                  type: getFileTypeLabel(uploadedFile.type),
                  size: getFileSizeLabel(uploadedFile.size)
                })}
              </div>
            </div>
            <button
              className="mobile-social-hint-detail-remove-file"
              onClick={() => handleFileRemove(sceneId)}
            >
              <img src={iconClose} alt={t('socialHint.common.alt.remove')} />
            </button>
          </div>
        ) : (
          <div className="mobile-social-hint-detail-upload-area">
            <input
              type="file"
              id={`upload_${sceneId}`}
              accept="video/*,image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(sceneId, file);
                }
              }}
              style={{ display: 'none' }}
            />
            <label
              htmlFor={`upload_${sceneId}`}
              className="mobile-social-hint-detail-upload-button"
            >
              <img src={iconUpload} alt={t('socialHint.common.alt.upload')} />
              <span>{t('socialHint.common.upload.cta', { label })}</span>
            </label>
          </div>
        )}
      </div>
    );
  };

  // Render scene instructions section
  const renderSceneInstructionsSection = () => {
    const scenes = getSceneInstructions();

    return (
      <div className="mobile-social-hint-detail-scenes">
        <h3 className="mobile-social-hint-detail-section-title">
          <span className="section-emoji">🎬</span>
          {t('socialHint.detail.sections.scenesTitle')}
        </h3>
        <div className="mobile-social-hint-detail-scenes-list">
          {scenes.map((scene) => (
            <div key={scene.id} className="mobile-social-hint-detail-scene-item">
              <div className="mobile-social-hint-detail-scene-header">
                <h4 className="mobile-social-hint-detail-scene-title">{scene.title}</h4>
              </div>
              <div className="mobile-social-hint-detail-scene-instruction">
                <p>{scene.instruction}</p>
              </div>
              {renderFileUpload(scene.id, t('socialHint.detail.upload.labels.scene'))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Get B-roll instructions
  const getBRollInstructions = useCallback(() => {
    const brollExamples = t('socialHint.detail.broll.examples', { returnObjects: true }) || {};
    const category = hint?.category;
    if (category && Array.isArray(brollExamples[category]) && brollExamples[category].length) {
      return brollExamples[category];
    }
    return brollExamples.default || [];
  }, [hint?.category, t]);

  // Render B-roll section
  const renderBRollSection = () => {
    const brollInstructions = getBRollInstructions();

    return (
      <div className="mobile-social-hint-detail-broll">
        <h3 className="mobile-social-hint-detail-section-title">
          <span className="section-emoji">🎞️</span>
          {t('socialHint.detail.sections.brollTitle')}
        </h3>
        <div className="mobile-social-hint-detail-broll-description">
          <p>{t('socialHint.detail.sections.brollDescription')}</p>
        </div>
        <div className="mobile-social-hint-detail-broll-list">
          {brollInstructions.map((broll) => (
            <div key={broll.id} className="mobile-social-hint-detail-broll-item">
              <div className="mobile-social-hint-detail-broll-header">
                <h4 className="mobile-social-hint-detail-broll-title">{broll.title}</h4>
              </div>
              <div className="mobile-social-hint-detail-broll-instruction">
                <p>{broll.instruction}</p>
              </div>
              {renderFileUpload(broll.id, t('socialHint.detail.upload.labels.broll'))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render embed section - copied from MobileSocialHint
  const renderEmbedSection = () => {
    if (!hint?.embedUrls || hint.embedUrls.length === 0) return null;

    return (
      <div className="mobile-social-hint-detail-preview">
        <h3 className="mobile-social-hint-detail-carousel-title">
          <span className="section-emoji">📱</span>
          {t('socialHint.common.embeds.title')}
        </h3>
        {hint.embedUrls && hint.embedUrls.length > 0 && hint.embedType === 'instagram' ? (
          <>
            <div className="mobile-social-hint-detail-carousel">
              <div
                className="mobile-social-hint-detail-carousel-container"
                style={{
                  transform: `translateX(-${(carouselStates[hint.id] || 0) * 100}%)`
                }}
              >
                {hint.embedUrls.map((embedUrl, embedIndex) => {
                  const embedKey = `detail_${hint.id}_${embedIndex}`;
                  return (
                    <div key={embedIndex} className="mobile-social-hint-detail-carousel-slide">
                      <div className="mobile-social-hint-detail-embed-container">
                        {embedsLoading[embedKey] && (
                          <div className="mobile-social-hint-detail-embed-loading">
                            <div className="mobile-social-hint-detail-spinner"></div>
                            <span>{t('socialHint.common.embeds.loading')}</span>
                          </div>
                        )}
                        <iframe
                          src={getInstagramEmbedUrl(embedUrl)}
                          className="mobile-social-hint-detail-embed-iframe"
                          frameBorder="0"
                          scrolling="no"
                          allowtransparency="true"
                          onLoad={() => handleEmbedLoad(embedKey)}
                          onError={() => handleEmbedError(embedKey)}
                          title={getIframeTitle(embedIndex, hint.title, hint.embedType)}
                        />
                        {/* Touch overlay for swipe gestures */}
                        <div
                          className="mobile-social-hint-detail-swipe-overlay"
                          onTouchStart={(e) => handleCarouselTouchStart(e, hint.id)}
                          onTouchMove={(e) => handleCarouselTouchMove(e)}
                          onTouchEnd={(e) => handleCarouselTouchEnd(e, hint.id, hint.embedUrls.length - 1)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Carousel indicators outside content */}
            {hint.embedUrls && hint.embedUrls.length > 1 && (
              <div className="mobile-social-hint-detail-external-indicators">
                {hint.embedUrls.map((_, dotIndex) => (
                  <button
                    key={dotIndex}
                    className={`mobile-social-hint-detail-external-dot ${
                      (carouselStates[hint.id] || 0) === dotIndex ? 'active' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCarouselDotClick(hint.id, dotIndex);
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="mobile-social-hint-detail-embed-placeholder">
            <div className="embed-preview-icon">
              {hint.embedType === 'instagram' ? '📷' : '🎵'}
            </div>
            <div className="embed-preview-text">
              <span className="embed-preview-type">{getEmbedTypeLabel(hint.embedType)}</span>
              <span className="embed-preview-label">{t('socialHint.common.embeds.placeholder')}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render loading state
  if (isLoading || !hint) {
    return (
      <div className="mobile-social-hint-detail">
        <div className="mobile-social-hint-detail-loading">
          <div className="mobile-social-hint-detail-spinner"></div>
          <p>{t('socialHint.common.status.loadingHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-social-hint-detail">
      <div className="mobile-social-hint-detail-content">
        {/* Header */}
        <div className="mobile-social-hint-detail-header">
          <button
            className="mobile-social-hint-detail-back"
            onClick={onBack}
          >
            <img src={iconArrowLeft} alt={t('socialHint.common.alt.back')} />
          </button>
          <h1 className="mobile-social-hint-detail-title">{hint.title}</h1>
        </div>

        {/* Error message */}
        {error && (
          <div className="mobile-social-hint-detail-error-banner">
            {error}
          </div>
        )}

        {/* Hint info */}
        <div className="mobile-social-hint-detail-info">
          <div className="mobile-social-hint-detail-description">
            <p>{hint.detailedDescription}</p>
          </div>
        </div>

        {/* Creation progress section */}
        {isCreating && (
          <div className="mobile-social-hint-detail-creation-section">
            <div className="mobile-social-hint-detail-creation-container">
              <div className="mobile-social-hint-detail-creation-title">
                <span className="section-emoji">🚀</span>
                {t('socialHint.detail.status.creating')}
              </div>
              <div className="mobile-social-hint-detail-progress-container-inline">
                <div className="mobile-social-hint-detail-progress-bar-inline">
                  <div
                    className="mobile-social-hint-detail-progress-fill-inline"
                    style={{ width: `${creationProgress}%` }}
                  />
                </div>
                <div className="mobile-social-hint-detail-progress-text-inline">
                  {t('socialHint.common.status.progressValue', {
                    progress: Math.round(creationProgress)
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Created video section */}
        {createdVideoUrl && !isCreating && (
          <div className="mobile-social-hint-detail-created-video-section">
            <div className="mobile-social-hint-detail-created-video-container">
              <div className="mobile-social-hint-detail-created-video-header">
                <h3 className="mobile-social-hint-detail-created-video-title">
                  <span className="section-emoji">🎬</span>
                  {t('socialHint.detail.status.createdTitle')}
                </h3>
              </div>
              <div className="mobile-social-hint-detail-video-preview-inline">
                <video
                  src={createdVideoUrl}
                  controls
                  className="mobile-social-hint-detail-created-video-inline"
                  poster="/path/to/video-thumbnail.jpg"
                >
                  {t('socialHint.detail.status.videoFallback')}
                </video>
              </div>
              <div className="mobile-social-hint-detail-video-actions-inline">
                <button className="mobile-social-hint-detail-video-action-button-inline download">
                  <img src={iconUpload} alt={t('socialHint.common.alt.download')} />
                  {t('socialHint.common.actions.download')}
                </button>
                <button
                  className={`mobile-social-hint-detail-video-action-button-inline queue ${isQueued ? 'queued' : ''}`}
                  onClick={handleQueueClick}
                  disabled={isQueued}
                >
                  {isQueued ? t('socialHint.common.actions.queued') : t('socialHint.common.actions.queue')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main embed section */}
        <div className="mobile-social-hint-detail-embed-section">
          {renderEmbedSection()}
        </div>

        {/* Scene instructions section */}
        <div className="mobile-social-hint-detail-section">
          {renderSceneInstructionsSection()}
        </div>

        {/* B-roll section */}
        <div className="mobile-social-hint-detail-section">
          {renderBRollSection()}
        </div>

        {/* Create content button */}
        <div className="mobile-social-hint-detail-section">
          <button
            className="mobile-social-hint-detail-create-button"
            onClick={handleCreateClick}
            disabled={uploadedFiles.size === 0}
          >
            <img src={iconAI} alt={t('socialHint.common.alt.ai')} className="mobile-social-hint-detail-create-button-icon" />
            {t('socialHint.common.actions.create')}
          </button>
        </div>

        {/* Bottom spacing */}
        <div className="mobile-social-hint-detail-bottom-spacing"></div>
      </div>

      {/* Create content modal */}
      {showCreateModal && (
        <div className="mobile-social-hint-detail-modal-overlay">
          <div className="mobile-social-hint-detail-modal">
            <div className="mobile-social-hint-detail-modal-header">
              <h3>{t('socialHint.detail.modal.create.title')}</h3>
              <button
                className="mobile-social-hint-detail-modal-close"
                onClick={handleCreateModalClose}
              >
                <img src={iconClose} alt={t('socialHint.common.alt.close')} />
              </button>
            </div>
            <div className="mobile-social-hint-detail-modal-content">
              <p>{t('socialHint.detail.modal.create.description')}</p>
              <div className="mobile-social-hint-detail-modal-files-count">
                <span>{t('socialHint.detail.modal.create.count', { count: uploadedFiles.size })}</span>
              </div>
              <p>{t('socialHint.detail.modal.create.question')}</p>
            </div>
            <div className="mobile-social-hint-detail-modal-actions">
              <button
                className="mobile-social-hint-detail-modal-button secondary"
                onClick={handleCreateModalClose}
              >
                {t('socialHint.detail.modal.create.cancel')}
              </button>
              <button
                className="mobile-social-hint-detail-modal-button primary"
                onClick={handleCreateConfirm}
              >
                {t('socialHint.detail.modal.create.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Queue confirmation modal */}
      {showQueueModal && (
        <div className="mobile-social-hint-detail-modal-overlay">
          <div className="mobile-social-hint-detail-modal">
            <div className="mobile-social-hint-detail-modal-header">
              <h3>{t('socialHint.detail.modal.queue.title')}</h3>
              <button
                className="mobile-social-hint-detail-modal-close"
                onClick={handleQueueModalClose}
              >
                <img src={iconClose} alt={t('socialHint.common.alt.close')} />
              </button>
            </div>
            <div className="mobile-social-hint-detail-modal-content">
              <div className="mobile-social-hint-detail-queue-success">
                <div className="mobile-social-hint-detail-queue-icon">✅</div>
                <p>{t('socialHint.detail.modal.queue.body')}</p>
                <div className="mobile-social-hint-detail-queue-info">
                  <span>{t('socialHint.detail.modal.queue.info')}</span>
                </div>
              </div>
            </div>
            <div className="mobile-social-hint-detail-modal-actions">
              <button
                className="mobile-social-hint-detail-modal-button primary"
                onClick={handleQueueModalClose}
              >
                {t('socialHint.detail.modal.queue.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSocialHintDetail;
